import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PromotionSettings.module.css';
import { Chip, Avatar, Grid, Typography } from '@mui/material';
import { AlertTriangle, Lock } from 'react-feather';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Card from '@/components/Card/Card';
import Table from '@/components/Tables/Table/Table';
import { GroupAndStatus, DeactivatePromotionPayload } from '@/types/promotions.types';
import { CellContext } from '@tanstack/react-table';
import Link from '@/components/Link/Link';
import { usePromotionsDeactivate, useStoresByGroupName } from '@/services/promotions/promotions.service';
import ModalStores from './internal/ModalStores.module';
import { SwitchCell } from './components/SwitchCell';
import { identicalArraysForPromotionSettings } from '@/services/promotions/promotions.helper.service';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Icon from '@/components/Icons/Icon';
import ConfirmCERPromosModal from '../components/ConfirmCERPromosModal/ConfirmCERPromosModal';
import TitleWithInfoIcon from '@/components/Title/TitleWithInfoIcon';

export interface PromotionSettingsProps {
  promotionId?: string;
  originalData: GroupAndStatus[];
  editToggles: boolean;
  isLock: boolean;
  isCER?: boolean;
  isVisible: boolean;
  showDeactivatePromoInfo?: boolean;
  existsActiveGlobalFreeze: boolean | undefined;
}

const ViewStoreGroup = ({ row }: CellContext<GroupAndStatus, unknown>) => {
  const [storeGroupName, setStoreGroupName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const { storeGroup } = row.original;
  const { data: stores } = useStoresByGroupName(!!storeGroupName, storeGroupName);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Link to={`/promotions/${storeGroup}`} />
      <Button
        variant={ButtonVariants.Text}
        onClick={() => {
          setStoreGroupName(storeGroup);
          setModalOpen(true);
        }}
      >
        {storeGroup}
      </Button>
      {modalOpen && (
        <ModalStores groupName={storeGroup} stores={stores} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
};
const PromotionSettings: FC<PromotionSettingsProps> = ({
  originalData,
  editToggles,
  promotionId,
  isLock,
  isCER = false,
  isVisible = true,
  showDeactivatePromoInfo = false,
  existsActiveGlobalFreeze,
}) => {
  const { t } = useTranslation();
  const [hideInactive, setHideInactive] = useState(false);
  const [disableSaveChangesButton, setDisableSaveChangesButton] = useState(false);
  const [currentData, setCurrentData] = useState<GroupAndStatus[]>([]);
  const { mutate, isLoading: updateIsLoading } = usePromotionsDeactivate(promotionId);
  const [showCERConfirmedModal, setShowCERConfirmedModal] = useState(false);
  const filteredPromoSettings = hideInactive ? currentData.filter((group) => !group.isHidden) : currentData;
  const noActiveGlobalFreeze = !existsActiveGlobalFreeze;

  useEffect(() => {
    setCurrentData(
      originalData.map((data) => ({
        ...data,
        isHidden: data.status === 'Inactive',
      })),
    );
  }, [originalData]);

  useEffect(() => {
    const identicalData = identicalArraysForPromotionSettings(originalData, currentData);
    setDisableSaveChangesButton(identicalData);
  }, [currentData, originalData]);

  const handleTextLinkClick = () => {
    setHideInactive(!hideInactive);
  };

  const renderIsVisibleCell = useMemo(() => {
    if (editToggles && !isLock && noActiveGlobalFreeze) {
      return SwitchCell;
    }

    return ({ row }: CellContext<GroupAndStatus, unknown>) => row.original.status;
  }, [editToggles, isLock]);

  const removeUnchangedGroupStatus = (data: GroupAndStatus, status: 'Active' | 'Inactive') => {
    const groupThatHaveChangedTheStatus = originalData.find(
      (original) => original.groupId === data.groupId && data.status === status && original.status !== data.status,
    );
    return groupThatHaveChangedTheStatus;
  };

  const onButtonClick = () => {
    const promosToUpdate: DeactivatePromotionPayload = {
      promotionIds: promotionId ? [promotionId] : [],
      addStoreGroupsIds: currentData
        .filter((promotionStatusGroup) => removeUnchangedGroupStatus(promotionStatusGroup, 'Active'))
        .map((groupStatus) => groupStatus.groupId),

      removeStoreGroupsIds: currentData
        .filter((promotionStatusGroup) => removeUnchangedGroupStatus(promotionStatusGroup, 'Inactive'))
        .map((groupStatus) => groupStatus.groupId),
    };

    mutate(promosToUpdate, {
      onSuccess: () => {
        if (isCER) {
          setShowCERConfirmedModal(true);
        } else {
          showToast({
            id: 'errorUpdatingPromoSettings',
            label: t('promotions.promotionsSettings.updateSuccessfully'),
          });
        }
      },
      onError: () => {
        showToast({
          id: 'errorUpdatingPromoSettings',
          label: t('promotions.promotionsSettings.updateFailure'),
          toastType: ToastTypes.Error,
        });
        setShowCERConfirmedModal(false);
      },
    });
  };

  const columns = [
    {
      id: 'storeGroup',
      accessorKey: 'storeGroup',
      header: t('promotions.promotionsSettings.table.storeGroup'),
      enableSorting: true,
      cell: ViewStoreGroup,
    },
    {
      id: 'status',
      cell: renderIsVisibleCell,
      accessorKey: 'status',
      header: t('promotions.promotionsSettings.table.active'),
      enableSorting: true,
    },
  ];
  return (
    <div className={styles.bottomContainer}>
      <Grid container direction="column" alignItems={{ sm: 'flex-start' }} gap={3}>
        <TitleWithInfoIcon title={t('promotions.promotionsSettings.header')} />
        {showDeactivatePromoInfo && (
          <Card className={styles.tooltipCard}>
            <Icon iconName="Info" />
            <div>{t('promotions.promotionsSettings.deactivatePromoInfo')}</div>
          </Card>
        )}
      </Grid>
      <Card>
        <div className={styles.tableHeader}>
          <Grid
            container
            direction="column"
            spacing={0}
            gap={0}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              paddingBottom: '0',
              marginBottom: '0',
            }}
          >
            <Grid
              item
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                paddingBottom: '0px',
                marginBottom: '0px',
              }}
            >
              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Grid item sm={8}>
                  <Grid container direction="row">
                    <Grid item lg>
                      <h3>{t('promotions.promotionsSettings.promoGroupAssignments')}</h3>
                    </Grid>
                    {isLock && (
                      <Grid item lg={6} md={8} sm={12} mb={0}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#5a5a5a',
                          }}
                        >
                          <Grid container mt={{ md: 2, lg: 0 }}>
                            <>
                              <Grid item pr={0.5}>
                                <Lock size={16} color="#e21216" strokeWidth={3} />
                              </Grid>
                              {t('promotions.promotionsSettings.promoCurrentlyLocked')}
                            </>
                          </Grid>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item>
                  <div className={styles.alignButton}>
                    <Button variant={ButtonVariants.Text} onClick={handleTextLinkClick}>
                      {hideInactive ? t('common.showInactive') : t('common.hideInactive')}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              {!isLock && isCER && (
                <Card className={styles.tooltipCardAlert}>
                  <AlertTriangle size={16} color="#e21216" strokeWidth={2} />
                  <div>{t('promotions.promotionsSettings.CERPromoDisclaimer')}</div>
                </Card>
              )}
            </Grid>
          </Grid>
        </div>
        <Table
          loadingRowCount={7}
          includeMinWidth={false}
          className={styles.cardWidth}
          data={filteredPromoSettings}
          columns={columns}
          meta={{
            columns,
            data: filteredPromoSettings,
            updateData: (rowIndex: number, columnId: string, value: string) => {
              setCurrentData((old) =>
                old?.map((row, index) => {
                  if (index === rowIndex) {
                    return {
                      ...old[rowIndex],
                      [columnId]: value,
                    };
                  }
                  return row;
                }),
              );
            },
          }}
        />
        <CalendarAlert className={styles.weeklyChangesBanner} warningIcon requestedVerbiage={false} />
        {!isLock && isVisible && noActiveGlobalFreeze && (
          <div className={styles.saveChanges}>
            <Button disabled={disableSaveChangesButton} isLoading={updateIsLoading} onClick={onButtonClick}>
              {t('promotions.promotionsSettings.saveButton')}
            </Button>
          </div>
        )}
      </Card>

      <ConfirmCERPromosModal
        isOpen={showCERConfirmedModal}
        onClose={() => setShowCERConfirmedModal(false)}
        onConfirm={() => setShowCERConfirmedModal(false)}
        isLoading={false}
      />
    </div>
  );
};

export default PromotionSettings;
